import { logError } from '../lib/logger';
import { isMediaModalOpen } from '../lib/mediaModal';
import { pollIntervalMs } from '../lib/pollJitter';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { userCanAccessBusiness } from '../lib/businessAccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-App-Client'] = 'academy';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

async function refreshSession() {
  await axios.post(`${API}/auth/refresh`);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingStatus, setPollingStatus] = useState(null);
  const pollerRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      try {
        await refreshSession();
      } catch {
        /* no valid session yet */
      }
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        try {
          const statusResp = await axios.get(`${API}/auth/status`);
          setUser(statusResp.data);
          setPollingStatus(statusResp.data.status);
        } catch (err) {
          logError('AuthContext', err);
          setUser(null);
        }
      } else {
        // Nota: il refresh è già stato tentato sopra prima di /auth/me, quindi un
        // 401 qui significa che il token non è valido: ritentare lo stesso refresh
        // non cambierebbe esito e raddoppierebbe solo le chiamate fallite.
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user) {
      if (pollerRef.current) {
        clearTimeout(pollerRef.current);
        pollerRef.current = null;
      }
      return;
    }

    const scheduleNext = () => {
      if (pollerRef.current) clearTimeout(pollerRef.current);
      const base = user.status === 'pending' ? 60000 : 600000;
      const jitter = user.status === 'pending' ? 15000 : 60000;
      pollerRef.current = window.setTimeout(async () => {
        try {
          const resp = await axios.get(`${API}/auth/status`);
          if (resp.data.status === 'disabled') {
            setUser(null);
            window.location.href = '/login';
            return;
          }
          if (resp.data.status !== user.status) {
            setUser(prev => ({ ...prev, status: resp.data.status }));
            setPollingStatus(resp.data.status);
            if (resp.data.status === 'approved') {
              const meResp = await axios.get(`${API}/auth/me`);
              setUser(meResp.data);
            }
          }
        } catch (err) {
          if (err.response?.status === 401) {
            try {
              await refreshSession();
              return;
            } catch {
              /* fall through to logout */
            }
            setUser(null);
            if (!isMediaModalOpen()) {
              window.location.href = '/login';
            }
            return;
          }
          logError('AuthContext', err);
        } finally {
          scheduleNext();
        }
      }, pollIntervalMs(base, jitter));
    };

    scheduleNext();
    return () => {
      if (pollerRef.current) {
        clearTimeout(pollerRef.current);
        pollerRef.current = null;
      }
    };
  }, [user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (email, password, name, sponsor = '', codice_incaricato = '') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, sponsor, codice_incaricato });
    if (response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      logError('AuthContext', err);
    }
    setUser(null);
  };

  const isAdmin = user?.role === 'admin' && user?.status === 'approved';
  const canAccessBusiness = userCanAccessBusiness(user);

  const value = {
    user,
    loading,
    pollingStatus,
    login,
    register,
    logout,
    isAuthenticated: !!user && user.status === 'approved',
    isAdmin,
    canAccessBusiness,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
